import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Pin, X, Mail, Phone, MapPin, Briefcase, Building2,
  LineChart, Star, Newspaper, MessageSquare, Smile,
  TrendingUp, TrendingDown, Minus, List, Network, ArrowUpRight,
  Receipt, Users as UsersIcon, CalendarDays, LayoutGrid,
  ExternalLink,
} from 'lucide-react';
import OrgChart from '../components/OrgChart';
import HeroCalendar from '../components/HeroCalendar';
import SlackLink from '../components/SlackLink';
import { useAuth } from '../contexts/AuthProvider';
import { useLocale, useT } from '../contexts/LocaleProvider';
import { useFavorites } from '../contexts/FavoritesContext';
import AttachmentList from '../components/announcements/AttachmentList';
import ReactionStrip from '../components/announcements/ReactionStrip';
import CommentList from '../components/announcements/CommentList';
import EmployeeAvatar from '../components/EmployeeAvatar';
import TopBarShell from '../components/TopBarShell';
import SurveyNudge from '../components/SurveyNudge';
import { AI_SURVEY } from '../lib/surveys/aiAtWork';
import { deptLabel } from '../lib/departments';
import { LT } from '../lib/appCatalogue';
import { useAppCatalogue } from '../contexts/AppCatalogueContext';
import { localizedPost } from '../lib/announcementLocale';
import { localizedApp } from '../lib/appLocale';
import { pickGreeting } from '../lib/greeting';
import api from '../utils/api';

/**
 * LampTwist hub · the company landing page. Rendered at /, wrapped by the
 * shared TopBarShell so every page in the app uses the same chrome.
 */

const Splash = () => {
  const { user } = useAuth();
  const t = useT();
  const { intlLocale, locale } = useLocale();
  const { favorites, saving: favSaving, toggle: toggleFavorite } = useFavorites();
  const [posts, setPosts] = useState(null);
  const [team, setTeam] = useState(null);
  const [salesKpis, setSalesKpis] = useState(null);  // null until first fetch, false if unavailable
  const [selectedPostId, setSelectedPostId] = useState(null);

  useEffect(() => {
    api.announcements().then(setPosts).catch(() => setPosts([]));
    api.employees().then(setTeam).catch(() => setTeam([]));
    // KPIs are best-effort · if Perfy DB isn't configured (503) or unreachable
    // we just hide the band rather than show a broken widget.
    api.salesKpis().then(setSalesKpis).catch(() => setSalesKpis(false));
  }, []);

  /* Hub-side reactions + comments · same endpoints as the HR feed, the
   * splash just renders them too now. Optimistic-ish: server returns the
   * fresh aggregate and we splice it in. */
  const updatePostInList = (id, patch) => setPosts(p =>
    (p || []).map(x => x.id === id ? { ...x, ...patch } : x)
  );
  const toggleReaction = async (postId, emoji) => {
    const { reactions } = await api.toggleReaction(postId, emoji);
    updatePostInList(postId, { reactions });
  };
  const postComment = async (postId, body) => {
    const c = await api.postComment(postId, body);
    setPosts(p => (p || []).map(x => x.id === postId
      ? { ...x, comments: [...(x.comments || []), c] }
      : x));
  };
  const deleteComment = async (postId, commentId) => {
    if (!window.confirm(t('home.confirmDeleteComment') || 'Supprimer ce commentaire ?')) return;
    await api.deleteComment(postId, commentId);
    setPosts(p => (p || []).map(x => x.id === postId
      ? { ...x, comments: (x.comments || []).filter(c => c.id !== commentId) }
      : x));
  };

  const firstName = (user?.displayName || '').split(' ')[0];
  const today = useMemo(
    () => new Intl.DateTimeFormat(intlLocale, { weekday: 'long', day: 'numeric', month: 'long' })
      .format(new Date()),
    [intlLocale],
  );
  const greeting = useMemo(() => pickGreeting(locale), [locale]);

  const sortedPosts = useMemo(() => (posts || []).slice().sort((a, b) =>
    Number(b.pinned) - Number(a.pinned) || new Date(b.published_at) - new Date(a.published_at)
  ), [posts]);
  const featured = useMemo(() => {
    if (selectedPostId != null) {
      const hit = sortedPosts.find(p => p.id === selectedPostId);
      if (hit) return hit;
    }
    return sortedPosts[0];
  }, [sortedPosts, selectedPostId]);
  const moreNews = useMemo(
    () => featured ? sortedPosts.filter(p => p.id !== featured.id) : sortedPosts,
    [sortedPosts, featured],
  );

  const { apps: catalogueApps } = useAppCatalogue();
  const bySlug = useMemo(() => {
    const m = new Map();
    catalogueApps.forEach(a => m.set(a.slug, a));
    return m;
  }, [catalogueApps]);
  const favApps = favorites.map(s => bySlug.get(s)).filter(Boolean);

  return (
    <TopBarShell>
      {/* Slim hero · greeting + date on the left, a compact
          upcoming-events strip on the right that fills the dead
          space alongside the headline. */}
      {/* The greeting takes only what it needs; the calendar beside it
          gets the rest of the width so its timeline can breathe. */}
      <header className="mb-6 flex items-start gap-10">
        <div className="shrink-0">
          <h1 className="font-display text-[2.25rem] sm:text-[2.75rem] leading-[1.05] tracking-[-0.025em]"
              style={{ color: LT.greenDeep }}>
            {greeting},{' '}
            <span className="italic font-normal">{firstName || 'LampTwist'}</span>.
          </h1>
          <p className="mt-2 text-[14px] font-num capitalize" style={{ color: LT.green50 }}>
            {today}
          </p>
        </div>
        <HeroCalendar className="hidden md:block flex-1 min-w-0 max-w-[640px] ml-auto" />
      </header>

      {/* Snelstart · the four shortcuts most people open more than once
          a day. Sits above the news so it's never below the fold. The
          first tile (Onkostennota's) is the primary action · darker
          surface to signpost it as the main job to be done. */}
      <Snelstart t={t} />
      <SurveyNudge survey={AI_SURVEY} />

      {/* Featured post + unified sidebar (KPIs · favorites · history) */}
      <section id="news" className="scroll-mt-24 mt-2 grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 flex">
          <FeaturedPost
            t={t}
            post={featured}
            loading={posts === null}
            intlLocale={intlLocale}
            locale={locale}
            myEmail={(user?.email || '').toLowerCase()}
            onToggleReaction={toggleReaction}
            onPostComment={postComment}
            onDeleteComment={deleteComment}
          />
        </div>

        <aside className="lg:col-span-4 p-6 space-y-5 lg:max-h-[760px] lg:overflow-y-auto"
               style={{ background: LT.beigeWarm }}>
          {salesKpis && <SidebarKpis t={t} kpis={salesKpis} intlLocale={intlLocale} />}
          {salesKpis && (
            <hr className="border-0 h-px" style={{ background: LT.green30 }} />
          )}

          <SidebarFavorites
            t={t}
            locale={locale}
            favApps={favApps}
            saving={favSaving}
            onUnpin={toggleFavorite}
          />
          <hr className="border-0 h-px" style={{ background: LT.green30 }} />

          <SidebarHistory
            t={t}
            intlLocale={intlLocale}
            posts={posts}
            sortedPosts={sortedPosts}
            moreNews={moreNews}
            onSelect={setSelectedPostId}
          />
        </aside>
      </section>


      {/* Team */}
      <TeamSection
        t={t}
        locale={locale}
        team={team}
      />
    </TopBarShell>
  );
};

/* ── Sidebar sections (sit inside the unified box) ─────────────── */

/* Four-tile snelstart row · most-used shortcuts up front so the home
 * screen feels actionable instead of a wall of news. The first tile
 * is the primary action (dark fill); the rest sit on the paper
 * surface with a hairline that strengthens on hover. */
const Snelstart = ({ t }) => {
  const tiles = [
    {
      id: 'expenses', primary: true, internal: '/expenses', icon: Receipt,
      label: t('snelstart.expenses.label'), sub: t('snelstart.expenses.sub'),
    },
    {
      id: 'directory', internal: '/directory', icon: UsersIcon,
      label: t('snelstart.directory.label'), sub: t('snelstart.directory.sub'),
    },
    {
      id: 'leave', external: 'https://time2.ileo.com/#/login', icon: CalendarDays,
      label: t('snelstart.leave.label'), sub: t('snelstart.leave.sub'),
    },
    {
      id: 'newsletter', internal: '/resources/newsletters', icon: Newspaper,
      label: t('snelstart.newsletter.label'), sub: t('snelstart.newsletter.sub'),
    },
    {
      id: 'apps', internal: '/apps', icon: LayoutGrid,
      label: t('snelstart.apps.label'), sub: t('snelstart.apps.sub'),
    },
  ];
  return (
    <section className="mb-5">
      <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-[0.14em] font-medium"
           style={{ color: LT.green50 }}>
        <ArrowUpRight className="h-3 w-3" />
        {t('snelstart.eyebrow')}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {tiles.map(tile => (
          <SnelstartTile key={tile.id} tile={tile} />
        ))}
      </div>
      <style>{SNEL_STYLE}</style>
    </section>
  );
};

/* All tiles share the cream surface so the row reads as a single
 * unit · the primary tile keeps a yellow icon disc as the only
 * "this is the main job" cue. Hover flips the whole tile to the
 * deep-green inverse via a scoped CSS class (Tailwind arbitrary
 * hover colors can be flaky in older configs). */
const SNEL_STYLE = `
  .snel-tile { background: ${LT.beigeLight}; border: 1px solid rgba(43,65,56,0.12); transition: background 200ms cubic-bezier(.2,0,0,1), border-color 200ms cubic-bezier(.2,0,0,1); }
  .snel-tile:hover { background: ${LT.greenDeep}; border-color: ${LT.greenDeep}; }
  .snel-tile .snel-label { color: ${LT.greenDeep}; transition: color 160ms ease; }
  .snel-tile .snel-sub   { color: ${LT.green50};  transition: color 160ms ease; }
  .snel-tile:hover .snel-label { color: ${LT.beigeLight}; }
  .snel-tile:hover .snel-sub   { color: rgba(251,251,250,0.6); }
`;

const SnelstartTile = ({ tile }) => {
  const { icon: Icon, primary, internal, external, label, sub } = tile;
  const className = "snel-tile group flex items-center gap-3 rounded-xl px-4 py-3";
  const inner = (
    <>
      <span className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
            style={{
              background: primary ? LT.yellow : LT.beigeWarm,
              color: LT.greenDeep,
            }}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="snel-label text-[14px] leading-tight truncate font-medium">{label}</div>
        <div className="snel-sub text-[10.5px] uppercase tracking-[0.12em] font-num truncate mt-0.5">{sub}</div>
      </div>
    </>
  );
  if (external) {
    return (
      <a href={external} target="_blank" rel="noreferrer" className={className}>{inner}</a>
    );
  }
  return (
    <Link to={internal} className={className}>{inner}</Link>
  );
};

const SidebarKpis = ({ t, kpis, intlLocale }) => {
  // The sales chart + top-mix blocks all read from perfy_orders /
  // perfy_order_line_items via SalesKpisView · credit the source so
  // readers know where to drill in for the full story.
  const { apps: catalogueApps } = useAppCatalogue();
  const perfy = useMemo(
    () => (catalogueApps || []).find(a => a.slug === 'perfy'),
    [catalogueApps],
  );
  const [view, setView] = useState('month');  // 'month' | 'week'
  const hasMonthly = Array.isArray(kpis.monthly) && kpis.monthly.length > 0;
  const hasWeekly  = Array.isArray(kpis.weekly)  && kpis.weekly.length  > 0;

  // Adapt the weekly rows to the shared chart shape.
  const monthSeries = hasMonthly ? kpis.monthly.map(m => ({
    key: `m-${m.month}`,
    label: null,         // computed by chart from locale + month #
    monthNum: m.month,
    ty: m.ty, ly: m.ly,
  })) : [];
  const weekSeries = hasWeekly ? kpis.weekly.map(w => ({
    key: `w-${w.bucket}`,
    label: `S${w.bucket}`,
    ty: w.ty, ly: w.ly,
  })) : [];

  return (
    <div>
      <div className="flex items-baseline gap-2">
        <LineChart className="h-3.5 w-3.5 shrink-0" style={{ color: LT.greenDeep }} />
        <span className="text-[11px] uppercase tracking-[0.14em] font-medium"
              style={{ color: LT.greenDeep }}>
          {t('splash.kpi.sales')}
        </span>
        {kpis.years && (
          <span className="ml-auto text-[10.5px]" style={{ color: LT.green50 }}>
            {kpis.years.ly} · {kpis.years.ty}
          </span>
        )}
      </div>

      {hasMonthly && hasWeekly && (
        <div className="mt-2 flex justify-center">
          <div className="inline-flex rounded-md text-[10.5px] overflow-hidden"
               style={{ border: `1px solid ${LT.green30}` }}>
            <KpiTab active={view === 'month'} onClick={() => setView('month')}>
              {t('splash.kpi.byMonth')}
            </KpiTab>
            <KpiTab active={view === 'week'} onClick={() => setView('week')}>
              {t('splash.kpi.byWeek')}
            </KpiTab>
          </div>
        </div>
      )}

      {view === 'month' && hasMonthly && (
        <BarChart
          t={t}
          series={monthSeries}
          years={kpis.years}
          currency={kpis.currency || 'EUR'}
          intlLocale={intlLocale}
          mode="month"
        />
      )}
      {view === 'week' && hasWeekly && (
        <BarChart
          t={t}
          series={weekSeries}
          years={kpis.years}
          currency={kpis.currency || 'EUR'}
          intlLocale={intlLocale}
          mode="week"
        />
      )}

      <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
        {/* MTD now compares month-to-date against last year up to the same
            day-and-hour (Perfy's live-report benchmark), so the YoY % is
            honest mid-month and we show it · the "LOPEND" tag just flags
            that the month is still in flight. YTD shares the same rendering. */}
        <KpiMetric label={t('splash.kpi.mtd')} pct={kpis.mtd?.pct_change}
                   inProgress t={t} />
        <KpiMetric label={t('splash.kpi.ytd')} pct={kpis.ytd?.pct_change} t={t} />
      </div>

      {((kpis.top_brands?.length > 0) || (kpis.top_products?.length > 0)) && (
        <TopMixBlocks
          t={t}
          intlLocale={intlLocale}
          currency={kpis.currency || 'EUR'}
          brandsBySales={kpis.top_brands || []}
          brandsByQty={kpis.top_brands_qty || []}
          productsBySales={kpis.top_products || []}
          productsByQty={kpis.top_products_qty || []}
        />
      )}

      {perfy && (
        <Link to={`/apps/${perfy.slug}`}
              className="mt-3 flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.12em] hover:opacity-80 transition"
              style={{ color: LT.green50 }}
              title={t('splash.kpi.poweredBy.hint')}>
          <span>{t('splash.kpi.poweredBy')}</span>
          {perfy.logo
            ? <img src={perfy.logo} alt="" className="h-3 w-3 object-contain shrink-0" />
            : <LineChart className="h-3 w-3" style={{ color: perfy.dot || LT.green50 }} />}
          <span className="font-medium" style={{ color: LT.greenDeep }}>Perfy</span>
        </Link>
      )}
    </div>
  );
};

/* Compact two-block layout · brands and products sit side by side in
 * the narrow splash sidebar. Each item is two lines: name (truncated)
 * + a single number line (qty · €) with a thin bar under it scaling
 * against the top seller for the active sort. Full numbers (incl.
 * exact €) live in the title tooltip for the curious. A € / # toggle
 * at the top swaps which ranking drives both blocks · the leaders
 * typically differ between the two metrics, so this matters. */
const TopMixBlock = ({ t, intlLocale, currency, title, items, sortBy }) => {
  if (!items.length) return null;
  const max = Math.max(
    ...items.map(i => Number((sortBy === 'qty' ? i.qty : i.sales) || 0)),
    1,
  );
  const fmtMoney = new Intl.NumberFormat(intlLocale, {
    style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1,
  });
  const fmtQty = new Intl.NumberFormat(intlLocale, { notation: 'compact', maximumFractionDigits: 1 });
  const fmtExact = new Intl.NumberFormat(intlLocale, { style: 'currency', currency });
  return (
    <div className="min-w-0">
      <div className="text-[9.5px] uppercase tracking-[0.12em] font-medium mb-1.5 truncate"
           style={{ color: LT.green50 }}>
        {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((it, idx) => {
          const metric = Number((sortBy === 'qty' ? it.qty : it.sales) || 0);
          const pct = Math.max(2, (metric / max) * 100);
          // Lead with the active metric (bolder); the secondary one
          // stays muted so admins still see both but the ranking
          // dimension is unambiguous.
          const primary = sortBy === 'qty'
            ? `${fmtQty.format(it.qty || 0)}${t('splash.kpi.top.qtySuffix')}`
            : fmtMoney.format(it.sales || 0);
          const secondary = sortBy === 'qty'
            ? fmtMoney.format(it.sales || 0)
            : `${fmtQty.format(it.qty || 0)}${t('splash.kpi.top.qtySuffix')}`;
          return (
            <li key={`${idx}-${it.name}`}
                title={`${it.name} · ${it.qty} × · ${fmtExact.format(it.sales || 0)}`}>
              <div className="text-[11px] leading-tight truncate" style={{ color: LT.greenDeep }}>
                {it.name}
              </div>
              <div className="flex items-center gap-1 mt-0.5">
                <div className="flex-1 h-[3px] rounded-full overflow-hidden"
                     style={{ background: LT.green30 }}>
                  <div className="h-full rounded-full" style={{ width: `${pct}%`, background: LT.greenDeep }} />
                </div>
                <span className="font-num text-[9.5px] shrink-0 tabular-nums" style={{ color: LT.green50 }}>
                  {secondary}
                </span>
                <span className="font-num text-[10px] font-medium shrink-0 tabular-nums" style={{ color: LT.greenDeep }}>
                  {primary}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const TopMixBlocks = ({ t, intlLocale, currency, brandsBySales, brandsByQty, productsBySales, productsByQty }) => {
  const [sortBy, setSortBy] = useState('sales');  // 'sales' (€) | 'qty' (#)
  const brands   = sortBy === 'qty' ? brandsByQty   : brandsBySales;
  const products = sortBy === 'qty' ? productsByQty : productsBySales;
  return (
    <div className="mt-3">
      <div className="flex justify-end mb-2">
        <div className="inline-flex rounded-md overflow-hidden text-[10px] font-num"
             style={{ border: `1px solid ${LT.green30}` }}>
          <KpiTab active={sortBy === 'sales'} onClick={() => setSortBy('sales')}>
            {t('splash.kpi.top.sortSales')}
          </KpiTab>
          <KpiTab active={sortBy === 'qty'} onClick={() => setSortBy('qty')}>
            {t('splash.kpi.top.sortQty')}
          </KpiTab>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <TopMixBlock t={t} intlLocale={intlLocale} currency={currency} sortBy={sortBy}
                     title={t('splash.kpi.top.brands')} items={brands} />
        <TopMixBlock t={t} intlLocale={intlLocale} currency={currency} sortBy={sortBy}
                     title={t('splash.kpi.top.products')} items={products} />
      </div>
    </div>
  );
};

const KpiTab = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="px-2.5 py-0.5 transition"
    style={{
      background: active ? LT.greenDeep : 'transparent',
      color: active ? LT.beigeLight : LT.green50,
      fontWeight: active ? 500 : 400,
    }}
  >
    {children}
  </button>
);

const MONTH_LETTERS = (intlLocale) => {
  // 12 single-letter month labels in the user's locale (J F M A M J J A S O N D in FR/EN).
  try {
    const fmt = new Intl.DateTimeFormat(intlLocale, { month: 'narrow' });
    return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2024, i, 1)));
  } catch {
    return ['J','F','M','A','M','J','J','A','S','O','N','D'];
  }
};

const BarChart = ({ t, series, years, currency, intlLocale, mode }) => {
  const monthLetters = useMemo(() => MONTH_LETTERS(intlLocale), [intlLocale]);
  const max = Math.max(1, ...series.flatMap(s => [s.ty, s.ly]));
  const fmtMoney = useMemo(
    () => new Intl.NumberFormat(intlLocale, {
      style: 'currency', currency, maximumFractionDigits: 0,
    }),
    [intlLocale, currency],
  );

  // Compact SVG · % delta label on top, axis label on the bottom.
  // Trimmed top/bot padding + slightly smaller font keeps the chart
  // tight when the brand/product blocks sit just below it.
  const w = 220, h = 72;
  const padX = 6, padTop = 9, padBot = 9;
  const slotW = (w - padX * 2) / Math.max(1, series.length);
  const barW  = Math.max(3, Math.min(8, (slotW - 4) / 2));
  const gap   = 1;
  const innerH = h - padTop - padBot;
  const baseline = h - padBot;

  // Flag the current month as "in flight" so we can texture its bar as
  // in-progress · only meaningful in 'month' mode. We no longer project a
  // full-month estimate: with the LY bar now capped at the same day-and-
  // hour, the to-date TY vs to-date LY pair is already a fair comparison,
  // so both bars just show their actual values so far.
  const now = new Date();
  const currentMonthNum = now.getMonth() + 1;
  const withPartial = series.map(s => ({
    ...s,
    partial: mode === 'month' && s.monthNum === currentMonthNum,
  }));

  return (
    <div className="mt-2">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" role="img"
           aria-label={t('splash.kpi.chartAria')}
           style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system' }}>
        <defs>
          {/* Diagonal hatch · textures the current month's bar so a glance
              reads it as "still filling in", without implying a projection. */}
          <pattern id="lt-bar-hatch" patternUnits="userSpaceOnUse"
                   width="3" height="3" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="3"
                  stroke={LT.beigeLight} strokeOpacity="0.55" strokeWidth="0.6" />
          </pattern>
        </defs>
        {withPartial.map((s, i) => {
          const x = padX + i * slotW + (slotW - (barW * 2 + gap)) / 2;
          const lyH = (s.ly / max) * innerH;
          const tyH = (s.ty / max) * innerH;
          const labelX = x + barW + gap / 2;
          // The in-flight month's LY bar is now capped at the same
          // day-and-hour, so its TY-to-date vs LY-to-date delta is a fair
          // comparison · show it like any other month, just textured to
          // signal the month isn't closed yet.
          const pct = s.ly > 0
            ? ((s.ty - s.ly) / s.ly) * 100
            : null;
          // Status colours per the brand handoff · muted sage for
          // positive deltas so the number reads as data, not a CTA;
          // brand danger red for negatives. Neutral for unknown.
          const pctColor = pct == null
            ? LT.green50
            : pct > 0 ? '#5f8a6f'
            : pct < 0 ? '#b54338'
            : LT.green50;
          const pctLabel = pct == null
            ? '·'
            : `${pct > 0 ? '+' : ''}${pct.toFixed(0)}%`;
          const axisLabel = s.label || (s.monthNum ? monthLetters[s.monthNum - 1] : '');
          return (
            <g key={s.key}>
              <title>
                {s.partial
                  ? `${axisLabel} · en cours · ${years?.ty}: ${fmtMoney.format(s.ty)} · ${years?.ly}: ${fmtMoney.format(s.ly)} (à la même date/heure)`
                  : `${axisLabel} · ${years?.ty}: ${fmtMoney.format(s.ty)} · ${years?.ly}: ${fmtMoney.format(s.ly)}`}
              </title>
              <text
                x={labelX} y={padTop - 3}
                textAnchor="middle"
                fontSize="5.5"
                fontWeight="500"
                fill={pctColor}
              >
                {pctLabel}
              </text>
              <rect
                x={x} y={baseline - lyH} width={barW} height={lyH}
                fill={LT.greenMid} rx="1.5"
              />
              {s.partial ? (
                <>
                  <rect
                    x={x + barW + gap} y={baseline - tyH} width={barW} height={tyH}
                    fill={LT.greenDeep} rx="1.5"
                  />
                  <rect
                    x={x + barW + gap} y={baseline - tyH} width={barW} height={tyH}
                    fill="url(#lt-bar-hatch)" rx="1.5"
                  />
                </>
              ) : (
                <rect
                  x={x + barW + gap} y={baseline - tyH} width={barW} height={tyH}
                  fill={LT.greenDeep} rx="1.5"
                />
              )}
              <text
                x={labelX} y={h - 3}
                textAnchor="middle"
                fontSize="5.5"
                fill={LT.green50}
              >
                {axisLabel}
              </text>
            </g>
          );
        })}
        <line
          x1={padX} x2={w - padX}
          y1={baseline + 0.5} y2={baseline + 0.5}
          stroke={LT.green30} strokeWidth="0.5"
        />
      </svg>
      {years && (
        <div className="mt-1 flex items-center justify-center gap-3 text-[10.5px] flex-wrap" style={{ color: LT.green50 }}>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm" style={{ background: LT.greenMid }} />
            {years.ly}
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 rounded-sm" style={{ background: LT.greenDeep }} />
            {years.ty}
          </span>
          {mode === 'month' && withPartial.some(s => s.partial) && (
            <span className="inline-flex items-center gap-1">
              <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden>
                <defs>
                  <pattern id="lt-legend-hatch" patternUnits="userSpaceOnUse"
                           width="3" height="3" patternTransform="rotate(45)">
                    <line x1="0" y1="0" x2="0" y2="3"
                          stroke={LT.beigeLight} strokeOpacity="0.7" strokeWidth="0.7" />
                  </pattern>
                </defs>
                <rect x="0.5" y="0.5" width="7" height="7" fill={LT.greenDeep} rx="1" />
                <rect x="0.5" y="0.5" width="7" height="7" fill="url(#lt-legend-hatch)" rx="1" />
              </svg>
              {t('splash.kpi.legend.inProgress')}
            </span>
          )}
          {mode === 'week' && (
            <span className="text-[10px]">· {t('splash.kpi.weekHint')}</span>
          )}
        </div>
      )}
    </div>
  );
};

const KpiMetric = ({ label, pct, inProgress, t }) => {
  const positive = typeof pct === 'number' && pct > 0;
  const negative = typeof pct === 'number' && pct < 0;
  const flat     = typeof pct === 'number' && pct === 0;
  const Icon     = positive ? TrendingUp : negative ? TrendingDown : Minus;
  // Status palette · muted sage for positives so the figure reads as
  // data, not a CTA; brand danger red for negatives. Neutral grey when
  // there's no baseline to compare against.
  const color    = positive ? '#5f8a6f' : negative ? '#b54338' : LT.green50;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
      style={{
        background: LT.beigeWarm,
        border: `1px solid ${LT.green30}`,
      }}
      title={inProgress ? t?.('splash.kpi.inProgress.hint') : undefined}
    >
      <span className="text-[10px] uppercase tracking-[0.12em] font-medium" style={{ color: LT.green50 }}>
        {label}{inProgress ? ` · ${t?.('splash.kpi.inProgress') || 'LOPEND'}` : ''}
      </span>
      {pct == null ? (
        <span className="text-[12px] font-num" style={{ color: LT.green50 }}>—</span>
      ) : (
        <span className="inline-flex items-center gap-0.5 text-[12.5px] font-medium font-num"
              style={{ color }}>
          <Icon className="h-3 w-3" />
          {positive ? '+' : ''}{flat ? '0' : pct.toFixed(1)}%
        </span>
      )}
    </span>
  );
};

const SidebarFavorites = ({ t, locale, favApps, saving, onUnpin }) => (
  <div>
    <SidebarHeader
      icon={Star}
      title={t('splash.fav.eyebrow')}
      iconFill={LT.yellowLight}
      note={saving ? t('common.saving') : null}
    />
    {favApps.length === 0 ? (
      <p className="mt-2 text-[12px]" style={{ color: LT.green50 }}>
        {t('splash.fav.emptyShort')}
      </p>
    ) : (
      <ul className="mt-2 flex flex-wrap gap-1.5">
        {favApps.map(app => (
          <FavoriteChip key={app.slug} app={app} t={t} locale={locale} onUnpin={() => onUnpin(app.slug)} />
        ))}
      </ul>
    )}
  </div>
);

const FavoriteChip = ({ app, t, locale, onUnpin }) => {
  const Icon = app.Icon || app.icon;
  const label = localizedApp(app, locale).name;
  const inner = (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[12.5px] transition hover:bg-black/[0.04]"
          style={{ color: LT.greenDeep, background: LT.beigeWarm, border: `1px solid ${LT.green30}` }}>
      {app.logo
        ? <img src={app.logo} alt="" className="h-3.5 w-3.5 object-contain" />
        : <Icon className="h-3.5 w-3.5" style={{ color: app.dot }} />}
      {label}
    </span>
  );
  return (
    <li className="group inline-flex items-center">
      {app.internal && app.to
        ? <Link to={app.to}>{inner}</Link>
        : <a href={app.href} target="_blank" rel="noreferrer">{inner}</a>}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onUnpin(); }}
        className="opacity-0 group-hover:opacity-100 transition p-0.5 -ml-0.5"
        title={t('splash.fav.unpin')}
        aria-label={t('splash.fav.unpin')}
      >
        <Star className="h-3 w-3" style={{ color: LT.greenDeep, fill: LT.yellowLight }} />
      </button>
    </li>
  );
};

const HISTORY_PREVIEW = 5;

const SidebarHistory = ({ t, intlLocale, locale, posts, sortedPosts, moreNews, onSelect }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? moreNews : moreNews.slice(0, HISTORY_PREVIEW);
  const canToggle = moreNews.length > HISTORY_PREVIEW;
  const hiddenCount = moreNews.length - HISTORY_PREVIEW;

  return (
    <div>
      <SidebarHeader
        icon={Newspaper}
        title={t('splash.news.history')}
        note={sortedPosts.length > 0 ? `${sortedPosts.length}` : null}
      />
      {moreNews.length === 0 ? (
        <p className="mt-2 text-[12px]" style={{ color: LT.green50 }}>
          {posts === null
            ? t('common.loading')
            : sortedPosts.length === 0
              ? t('splash.news.empty')
              : t('splash.news.onlyOne')}
        </p>
      ) : (
        <>
          <ul className="mt-1 divide-y -mx-1" style={{ borderColor: LT.green30 }}>
            {visible.map(p => (
              <SidebarRow
                key={p.id}
                post={p}
                intlLocale={intlLocale}
                locale={locale}
                t={t}
                onSelect={() => onSelect(p.id)}
              />
            ))}
          </ul>
          {canToggle && (
            <button
              type="button"
              onClick={() => setShowAll(v => !v)}
              className="mt-2 text-[12px] inline-flex items-center gap-1 transition hover:opacity-70"
              style={{ color: LT.greenDeep }}
            >
              {showAll
                ? t('splash.news.collapse')
                : t('splash.news.showAll', { n: hiddenCount })}
            </button>
          )}
        </>
      )}
    </div>
  );
};

const SidebarHeader = ({ icon: Icon, title, note, iconFill }) => (
  <div className="flex items-baseline gap-2">
    <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: LT.greenDeep, fill: iconFill || 'transparent' }} />
    <span className="text-[11px] uppercase tracking-[0.14em] font-medium"
          style={{ color: LT.greenDeep }}>
      {title}
    </span>
    {note && (
      <span className="ml-auto text-[11px]" style={{ color: LT.green50 }}>{note}</span>
    )}
  </div>
);

/* ── Featured post · full body ──────────────────────────────────── */

const FeaturedPost = ({ t, post, loading, intlLocale, locale, myEmail, onToggleReaction, onPostComment, onDeleteComment }) => {
  const [convoOpen, setConvoOpen] = useState(false);
  // Reset the expand state whenever a different post becomes the featured one.
  useEffect(() => { setConvoOpen(false); }, [post?.id]);
  if (loading) {
    return (
      <article className="rounded-2xl p-8 text-[13px]"
               style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}`, color: LT.green50 }}>
        {t('common.loading')}
      </article>
    );
  }
  if (!post) {
    return (
      <article className="rounded-2xl p-10"
               style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}` }}>
        <div className="text-[11px] uppercase tracking-[0.16em] font-medium" style={{ color: LT.green50 }}>
          {t('splash.news.eyebrow')}
        </div>
        <h2 className="font-display text-[1.4rem] mt-1" style={{ color: LT.greenDeep }}>
          {t('splash.news.title')}
        </h2>
        <p className="mt-2 text-[14px]" style={{ color: LT.green50 }}>{t('splash.news.empty')}</p>
      </article>
    );
  }

  const date = post.published_at
    ? new Intl.DateTimeFormat(intlLocale, { day: 'numeric', month: 'long', year: 'numeric' })
        .format(new Date(post.published_at))
    : '';
  const author = post.author_full_name || post.author_email || '';
  const { title: localTitle, body: localBody } = localizedPost(post, locale);
  const body = (localBody || '').trim();

  return (
    <article className="relative p-8 sm:p-10 w-full"
             style={{
               background: LT.beigeLight,
               border: '1px solid rgba(43,65,56,0.12)',
               borderTop: `2px solid ${LT.yellow}`,
             }}>
      <div className="relative">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.16em] font-medium">
          {post.pinned && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                  style={{ background: LT.yellow, color: LT.greenDeep }}>
              <Pin className="h-3 w-3" /> {t('splash.news.pinned')}
            </span>
          )}
          <span style={{ color: LT.greenDeep }}>{t('splash.news.eyebrow')}</span>
        </div>

        <h2 className="font-display text-[1.85rem] sm:text-[2.15rem] leading-[1.1] tracking-tight mt-3"
            style={{ color: LT.greenDeep }}>
          {localTitle || t('splash.news.untitled')}
        </h2>

        <div className="mt-3 flex items-center gap-2 text-[12.5px]" style={{ color: LT.green50 }}>
          <span>{author}</span>
          {date && <><span>·</span><span className="font-num">{date}</span></>}
        </div>

        {(post.video_url || '').trim() && (
          // The video leads the post · full width of the card, 16:9, native
          // controls. `#t=0.5` makes browsers paint a first frame as poster.
          <video src={`${post.video_url}#t=0.5`} controls preload="metadata" playsInline
                 className="mt-6 w-full block"
                 style={{ aspectRatio: '16 / 9', background: '#0B0A0D' }} />
        )}

        {body && (
          <div className="mt-6 text-[15px] leading-relaxed whitespace-pre-line"
               style={{ color: '#3a4f44' }}>
            {body}
          </div>
        )}

        {post.attachments && post.attachments.length > 0 && (
          <div>
            <AttachmentList attachments={post.attachments} />
          </div>
        )}

        {/* Optional call-to-action · only shown when the author filled
            both the label and the URL in the composer. Opens in a new
            tab so internal readers don't lose the splash context. */}
        {(post.cta_label || '').trim() && (post.cta_url || '').trim() && (
          <div className="mt-5">
            <a href={post.cta_url} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13.5px] font-medium transition hover:opacity-90"
               style={{ background: LT.greenDeep, color: LT.beigeLight }}>
              {post.cta_label}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        )}

        {/* Reactions + comments · the summary chip stays inline so the post
             height never shifts. Full thread opens in a modal so the
             layout stays aligned with the right-side sidebar. */}
        <div className="mt-5">
          <ConvoSummary
            t={t}
            reactions={post.reactions || []}
            commentCount={(post.comments || []).length}
            onOpen={() => setConvoOpen(true)}
          />
        </div>
      </div>
      {convoOpen && (
        <ConvoModal
          t={t}
          post={post}
          myEmail={myEmail}
          onToggleReaction={onToggleReaction}
          onPostComment={onPostComment}
          onDeleteComment={onDeleteComment}
          onClose={() => setConvoOpen(false)}
        />
      )}
    </article>
  );
};

/* Modal that hosts the full ReactionStrip + CommentList · opens on
 * "Réagir / commenter" click so the inline post never grows past the
 * sidebar's height. Backdrop-click and Escape both close. */
const ConvoModal = ({ t, post, myEmail, onToggleReaction, onPostComment, onDeleteComment, onClose }) => {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(20, 30, 25, 0.45)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[640px] rounded-2xl p-6 max-h-[85vh] overflow-y-auto"
        style={{
          background: LT.beigeLight,
          border: `1px solid ${LT.green30}`,
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-md transition hover:bg-black/[0.05]"
          aria-label={t('common.close') || 'Close'}
        >
          <X className="h-4 w-4" style={{ color: LT.green50 }} />
        </button>
        <h3 className="font-display text-[1.15rem] leading-tight mb-3" style={{ color: LT.greenDeep }}>
          {t('splash.news.react')}
        </h3>
        <ReactionStrip
          reactions={post.reactions || []}
          onToggle={(emoji) => onToggleReaction && onToggleReaction(post.id, emoji)}
        />
        <div className="mt-4">
          <CommentList
            comments={post.comments || []}
            myEmail={myEmail}
            onPost={(body) => onPostComment && onPostComment(post.id, body)}
            onDelete={(commentId) => onDeleteComment && onDeleteComment(post.id, commentId)}
          />
        </div>
      </div>
    </div>
  );
};

/* Compact "X réactions · Y commentaires · Réagir / commenter" row that
 * lives inline on the post. Click opens the modal · the post itself
 * never grows so the side-by-side layout stays aligned. */
const ConvoSummary = ({ t, reactions, commentCount, onOpen }) => {
  const reactionCount = (reactions || []).reduce((n, r) => n + (r.count || 0), 0);
  const topEmojis = (reactions || []).slice(0, 3).map(r => r.emoji);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="inline-flex items-center gap-3 text-[12.5px] rounded-md px-3 py-1.5 transition hover:bg-black/[0.04]"
      style={{ color: LT.greenDeep, border: `1px solid ${LT.green30}`, background: LT.beigeLight }}
    >
      <span className="inline-flex items-center gap-1.5">
        {topEmojis.length > 0
          ? <span className="font-num">{topEmojis.join(' ')}</span>
          : <Smile className="h-3.5 w-3.5" style={{ color: LT.green50 }} />}
        <span style={{ color: LT.green50 }}>
          {reactionCount} {t('splash.news.reactions')}
        </span>
      </span>
      <span aria-hidden style={{ color: LT.green30 }}>·</span>
      <span className="inline-flex items-center gap-1.5">
        <MessageSquare className="h-3.5 w-3.5" style={{ color: LT.green50 }} />
        <span style={{ color: LT.green50 }}>
          {commentCount} {t('splash.news.comments')}
        </span>
      </span>
      <span aria-hidden style={{ color: LT.green30 }}>·</span>
      <span style={{ color: LT.greenDeep, fontWeight: 500 }}>
        {t('splash.news.react')}
      </span>
    </button>
  );
};

const SidebarRow = ({ post, intlLocale, locale, t, onSelect }) => {
  const date = post.published_at
    ? new Intl.DateTimeFormat(intlLocale, { day: 'numeric', month: 'short' }).format(new Date(post.published_at))
    : '';
  const { title: localTitle, body: localBody } = localizedPost(post, locale);
  const snippet = (localBody || '').replace(/\s+/g, ' ').slice(0, 90);
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className="group w-full text-left flex items-start gap-2 px-1 py-3 transition hover:opacity-90"
        style={{ borderColor: LT.green30 }}
      >
        {post.pinned && <Pin className="h-3 w-3 mt-1 shrink-0" style={{ color: LT.greenDeep }} />}
        <div className="flex-1 min-w-0">
          <h4 className="text-[13px] font-medium leading-snug line-clamp-2 group-hover:underline"
              style={{ color: LT.greenDeep }}>
            {localTitle || t('splash.news.untitled')}
          </h4>
          {snippet && (
            <p className="mt-0.5 text-[12px] line-clamp-2" style={{ color: LT.green50 }}>
              {snippet}{(localBody || '').length > 90 ? '…' : ''}
            </p>
          )}
          <div className="mt-1 text-[10.5px] font-num" style={{ color: LT.green50 }}>{date}</div>
        </div>
      </button>
    </li>
  );
};

/* ── Team ───────────────────────────────────────────────────────── */

const TeamSection = ({ t, locale, team }) => {
  const [q, setQ] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [openEmp, setOpenEmp] = useState(null);
  const [view, setView] = useState('grid');  // 'grid' | 'org'

  // Externals are tracked in the admin but don't belong on the splash
  // team grid · they're third-party collaborators, not LampTwist staff.
  // Same exclusion carries through to the dept pills, search and the
  // org chart so the splash stays "your colleagues only".
  const active = useMemo(
    () => (team || []).filter(e => e.is_active !== false && !e.is_external),
    [team],
  );

  // Group active employees by department · the department's manager first,
  // then alpha by name. "Manager of the department" = a member at least one
  // colleague of the same group reports to (Mathilde in Service Client,
  // Arnaud in Finance). Managing people in *other* departments doesn't
  // float you — so Direction Générale itself stays in plain alpha order.
  const grouped = useMemo(() => {
    const byDept = new Map();
    active.forEach(e => {
      const key = e.departement || '__none__';
      if (!byDept.has(key)) byDept.set(key, []);
      byDept.get(key).push(e);
    });
    byDept.forEach(list => {
      const deptMgrs = new Set(
        list.map(e => (e.manager_email || '').toLowerCase()).filter(Boolean),
      );
      list.sort((a, b) => {
        const aIsMgr = deptMgrs.has((a.email || '').toLowerCase()) ? 0 : 1;
        const bIsMgr = deptMgrs.has((b.email || '').toLowerCase()) ? 0 : 1;
        if (aIsMgr !== bIsMgr) return aIsMgr - bIsMgr;
        return (a.full_name || '').localeCompare(b.full_name || '');
      });
    });
    const labelFor = (k) => k === '__none__' ? t('splash.team.noDept') : deptLabel(k, locale);
    return Array.from(byDept.entries())
      .sort(([a], [b]) => labelFor(a).localeCompare(labelFor(b)));
  }, [active, locale, t]);

  const departments = useMemo(() => grouped.map(([k]) => k), [grouped]);

  const needle = q.trim().toLowerCase();
  const matches = (e) =>
    !needle
    || (e.full_name || '').toLowerCase().includes(needle)
    || (e.email || '').toLowerCase().includes(needle)
    || (e.poste || '').toLowerCase().includes(needle);

  const visibleGroups = useMemo(() => grouped
    .filter(([k]) => !deptFilter || k === deptFilter)
    .map(([k, list]) => [k, list.filter(matches)])
    .filter(([, list]) => list.length > 0),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [grouped, deptFilter, needle]);

  const totalVisible = useMemo(
    () => visibleGroups.reduce((n, [, list]) => n + list.length, 0),
    [visibleGroups],
  );

  // Lock the grid's height to the natural unfiltered render so typing into
  // the search box doesn't shrink the page · readers complained that the
  // layout "jumps" with every keystroke, which was actually the document
  // height collapsing and pulling the scroll position with it. We capture
  // the height once the unfiltered grid has painted, then re-measure on
  // resize (window or container) so the lock stays accurate. The lock
  // releases when there's nothing to filter (no query + no dept pill).
  const gridRef = useRef(null);
  const [minGridH, setMinGridH] = useState(0);
  const isFiltering = !!(needle || deptFilter);
  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el || isFiltering) return;
    // Only refresh the lock when the grid is showing its full content ·
    // any smaller measurement would let the page shrink later.
    const h = el.offsetHeight;
    if (h > 0 && h !== minGridH) setMinGridH(h);
  }, [grouped, isFiltering, minGridH]);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const el = gridRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (!isFiltering) setMinGridH(el.offsetHeight);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [isFiltering]);

  return (
    <section id="team" className="mt-12 scroll-mt-24">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-display text-[1.4rem] leading-tight inline-flex items-center gap-3"
            style={{ color: LT.greenDeep }}>
          {t('splash.team.title')}
          <span className="inline-block h-2.5 w-16 rounded-full"
                style={{ background: LT.yellow }} aria-hidden />
        </h2>
        <div className="flex-1 h-px" style={{ background: LT.green30 }} />
        <span className="text-[12px] font-num" style={{ color: LT.green50 }}>
          {active.length} {t('splash.team.count')}
        </span>
        <ViewToggle
          value={view}
          onChange={setView}
          gridLabel={t('directory.view.list')}
          orgLabel={t('directory.view.org')}
        />
      </div>

      {team === null ? (
        <div className="rounded-2xl p-6 text-[13px]"
             style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}`, color: LT.green50 }}>
          {t('common.loading')}
        </div>
      ) : active.length === 0 ? (
        <div className="rounded-2xl p-6 text-[13px]"
             style={{ background: LT.beigeLight, border: `1px dashed ${LT.green30}`, color: LT.green50 }}>
          {t('splash.team.empty')}
        </div>
      ) : view === 'org' ? (
        <OrgChart employees={active} />
      ) : (
        <>
          {/* Search + dept pills */}
          <div className="mb-4 space-y-3">
            <input
              type="search"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder={t('splash.team.searchPh')}
              className="rounded-md px-3 py-1.5 text-[13px] outline-none w-full max-w-[320px]"
              style={{
                background: LT.beigeLight,
                border: `1px solid ${LT.green30}`,
                color: LT.greenDeep,
              }}
            />
            <div className="flex flex-wrap items-center gap-1.5">
              <DeptPill
                active={!deptFilter}
                label={t('splash.team.allDepts')}
                onClick={() => setDeptFilter('')}
              />
              {departments.map(d => (
                <DeptPill
                  key={d}
                  active={deptFilter === d}
                  label={d === '__none__' ? t('splash.team.noDept') : deptLabel(d, locale)}
                  onClick={() => setDeptFilter(d)}
                />
              ))}
              {needle && (
                <span className="ml-1 text-[12px] font-num" style={{ color: LT.green50 }}>
                  · {totalVisible} {t('splash.team.results')}
                </span>
              )}
            </div>
          </div>

          <div ref={gridRef} style={{ minHeight: minGridH || undefined }}>
          {visibleGroups.length === 0 ? (
            <p className="text-[13px]" style={{ color: LT.green50 }}>{t('splash.team.noMatch')}</p>
          ) : (
            <div className="space-y-6">
              {visibleGroups.map(([key, list]) => (
                <div key={key}>
                  <div className="flex items-baseline gap-3 mb-2">
                    <div className="text-[11px] uppercase tracking-[0.16em] font-medium"
                         style={{ color: LT.green50 }}>
                      {key === '__none__' ? t('splash.team.noDept') : deptLabel(key, locale)}
                    </div>
                    <div className="flex-1 h-px" style={{ background: LT.green30 }} />
                    <div className="text-[11px] font-num" style={{ color: LT.green50 }}>{list.length}</div>
                  </div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {list.map(e => (
                      <TeamCard
                        key={e.id} emp={e} locale={locale}
                        managerLabel={t('splash.team.manager')}
                        onOpen={() => setOpenEmp(e)}
                      />
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
          </div>
        </>
      )}

      {openEmp && (
        <TeamMemberModal
          emp={openEmp}
          team={active}
          locale={locale}
          t={t}
          onClose={() => setOpenEmp(null)}
          onJumpTo={(e) => setOpenEmp(e)}
        />
      )}
    </section>
  );
};

const DeptPill = ({ active, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="px-3 py-1 rounded-full text-[12.5px] transition"
    style={{
      background: active ? LT.greenDeep : LT.beigeLight,
      color: active ? LT.beigeLight : LT.greenDeep,
      border: `1px solid ${active ? LT.greenDeep : LT.green30}`,
    }}
  >
    {label}
  </button>
);

const TeamCard = ({ emp, locale, managerLabel, onOpen }) => {
  const managerName = emp.manager?.full_name || emp.manager_name || '';
  return (
    <li className="relative">
      <button
        type="button"
        onClick={onOpen}
        className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-black/[0.02]"
        style={{ background: LT.beigeLight, border: `1px solid ${LT.green30}` }}
      >
        <EmployeeAvatar fullName={emp.full_name} email={emp.email} photo={emp.photo} size={36} />
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-medium truncate" style={{ color: LT.greenDeep }}>
            {emp.full_name}
          </div>
          <div className="text-[12px] truncate" style={{ color: LT.green50 }}>
            {emp.poste || (emp.departement ? deptLabel(emp.departement, locale) : '·')}
          </div>
          <div className="flex items-center gap-1 text-[11px] mt-0.5 truncate"
               style={{ color: LT.green50 }}
               title={managerName ? `${managerLabel}: ${managerName}` : undefined}>
            {managerName ? (
              <>
                <ArrowUpRight className="h-3 w-3 shrink-0" />
                <span className="truncate">{managerName}</span>
              </>
            ) : (
              <span className="invisible">·</span>
            )}
          </div>
        </div>
      </button>
      {/* Floats over the card so the button stays a single tap target. */}
      <SlackLink
        slackUserId={emp.slack_user_id}
        size={14}
        className="absolute top-2 right-2 p-1"
        title="Slack"
      />
    </li>
  );
};

const ViewToggle = ({ value, onChange, gridLabel, orgLabel }) => (
  <div className="inline-flex items-center rounded-md overflow-hidden text-[12px]"
       style={{ border: `1px solid ${LT.green30}` }}>
    <button type="button" onClick={() => onChange('grid')}
            aria-pressed={value === 'grid'}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 transition"
            style={{
              background: value === 'grid' ? LT.greenDeep : 'transparent',
              color: value === 'grid' ? LT.beigeLight : LT.greenDeep,
            }}>
      <List className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{gridLabel}</span>
    </button>
    <button type="button" onClick={() => onChange('org')}
            aria-pressed={value === 'org'}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 transition border-l"
            style={{
              background: value === 'org' ? LT.greenDeep : 'transparent',
              color: value === 'org' ? LT.beigeLight : LT.greenDeep,
              borderColor: LT.green30,
            }}>
      <Network className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{orgLabel}</span>
    </button>
  </div>
);

const TeamMemberModal = ({ emp, team, locale, t, onClose, onJumpTo }) => {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const manager = emp.manager_email
    ? team.find(x => (x.email || '').toLowerCase() === emp.manager_email.toLowerCase())
    : null;
  const reports = team.filter(x =>
    (x.manager_email || '').toLowerCase() === (emp.email || '').toLowerCase()
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(20, 30, 25, 0.45)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[480px] rounded-2xl p-6"
        style={{
          background: LT.beigeLight,
          border: `1px solid ${LT.green30}`,
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        }}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-md transition hover:bg-black/[0.05]"
          aria-label={t('common.close') || 'Close'}
        >
          <X className="h-4 w-4" style={{ color: LT.green50 }} />
        </button>

        <div className="flex items-center gap-4">
          <EmployeeAvatar fullName={emp.full_name} email={emp.email} photo={emp.photo} size={64} />
          <div className="min-w-0 flex-1">
            <div className="font-display text-[1.35rem] leading-tight truncate"
                 style={{ color: LT.greenDeep }}>
              {emp.full_name}
            </div>
            {emp.poste && (
              <div className="text-[13px] mt-0.5 truncate" style={{ color: LT.green50 }}>
                {emp.poste}
              </div>
            )}
          </div>
          <SlackLink slackUserId={emp.slack_user_id} size={20}
                     className="p-1.5 rounded-md hover:bg-black/[0.04]" />
        </div>

        <dl className="mt-5 space-y-2.5 text-[13.5px]">
          {emp.departement && (
            <ModalRow icon={Building2} label={t('splash.team.dept')}
                      value={deptLabel(emp.departement, locale)} />
          )}
          {emp.email && (
            <ModalRow icon={Mail} label={t('splash.team.email')}
                      value={emp.email}
                      href={`mailto:${emp.email}`} />
          )}
          {emp.telephone && (
            <ModalRow icon={Phone} label={t('splash.team.phone')}
                      value={emp.telephone}
                      href={`tel:${emp.telephone.replace(/\s+/g, '')}`} />
          )}
          {emp.localisation && (
            <ModalRow icon={MapPin} label={t('splash.team.office')}
                      value={emp.localisation} />
          )}
          {(manager || emp.manager_name) && (
            <ModalRow
              icon={Briefcase}
              label={t('splash.team.manager')}
              value={manager ? manager.full_name : emp.manager_name}
              onClick={manager ? () => onJumpTo(manager) : undefined}
            />
          )}
        </dl>

        {reports.length > 0 && (
          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-[0.16em] font-medium mb-2"
                 style={{ color: LT.green50 }}>
              {t('splash.team.reports')} · {reports.length}
            </div>
            <ul className="flex flex-wrap gap-1.5">
              {reports.map(r => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => onJumpTo(r)}
                    className="inline-flex items-center gap-2 px-2 py-1 rounded-full transition hover:bg-black/[0.04]"
                    style={{ background: LT.beigeWarm, border: `1px solid ${LT.green30}` }}
                  >
                    <EmployeeAvatar fullName={r.full_name} email={r.email} photo={r.photo} size={20} />
                    <span className="text-[12px]" style={{ color: LT.greenDeep }}>
                      {r.full_name}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const ModalRow = ({ icon: Icon, label, value, href, onClick }) => {
  const valueEl = (
    <span className="text-[13.5px]" style={{ color: LT.greenDeep }}>
      {value}
    </span>
  );
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: LT.green50 }} />
      <span className="text-[12px] w-24 shrink-0" style={{ color: LT.green50 }}>{label}</span>
      {href ? (
        <a href={href} className="hover:underline truncate">{valueEl}</a>
      ) : onClick ? (
        <button type="button" onClick={onClick} className="text-left hover:underline truncate">
          {valueEl}
        </button>
      ) : (
        <span className="truncate">{valueEl}</span>
      )}
    </div>
  );
};

export default Splash;
